import { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import {
    BusSchedulePresenter,
    ReadPresenter4,
    useGetTransportAvailableDaysQuery,
    useGetTransportSchedulesQuery,
} from 'store/api-slice/hooks'
import {
    TransportDepartureType,
    TransportRouteType,
} from 'store/api-slice/types'
import { RouteType } from 'store/transport-slice'
import { useApp } from 'store/steps'
import { useAutoScroll } from 'utils/hooks'
import Box from 'components/shared/Box'
import { OptionProps } from 'components/shared/Select'
import BusesCalendarModal from 'components/global/CalendarModal/forks/BusesCalendarModal'
import { getLaterTime } from 'helpers/helpers'
import { ErrorField, handleError } from 'helpers/errors'
import { BusDateAndHour } from './BusDateAndHours'
import { TransportBlockingError } from 'routes/Upsell/types'

export interface RouteDetails {
    routeIndex?: number
    duration?: string
    unitPrice?: number
    productId?: string
    isRequiresPurchaseTimeSlot?: boolean
}
export type OriginalSettings = {
    hour: string
    quantity: number
    isRequiresPurchaseTimeSlot?: boolean
}

interface BusDatesAndHoursProps {
    routeIndex: number
    routeId: string | number
    routeType: TransportRouteType
    departurePlace: TransportDepartureType
    ticketsQuantity: number
    dateTo?: string
    dateFrom?: string
    setDateTo?: (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => void
    setDateFrom?: (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => void
    timeTo?: string | null
    timeFrom?: string | null
    setTimeTo: (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => void
    setTimeFrom: (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => void
    setError?: (errorType: TransportBlockingError) => void
    setSeatsLimit: (limit: number, direction: string) => void
    isBookingConflictError: boolean
    setRouteDetails: (data: RouteDetails) => void
    edited?: {
        index: number
        origin: RouteType
    } | null
    isEditable?: boolean
    setIsEditable?: (isEditable: boolean) => void
    isTimeSlotChangeable?: boolean
    setIsTimeSlotChangeable?: (isTimeSlotChangeable: boolean) => void
    testIdPrefix?: string
    isRequiresTimeSlotOriginal?: boolean
}

type DirectionType = 'to_park' | 'from_park'

export const BusDatesAndHours = ({
    routeIndex,
    routeId,
    routeType,
    departurePlace,
    ticketsQuantity,
    dateTo,
    dateFrom,
    setDateTo,
    setDateFrom,
    timeTo,
    timeFrom,
    setTimeTo,
    setTimeFrom,
    setError,
    setSeatsLimit,
    isBookingConflictError,
    setRouteDetails,
    edited,
    isEditable,
    setIsEditable,
    isTimeSlotChangeable,
    setIsTimeSlotChangeable,
    testIdPrefix,
    isRequiresTimeSlotOriginal,
}: BusDatesAndHoursProps) => {
    const { language, isCreatorMode } = useApp()
    const { formatMessage } = useIntl()
    const { animatePendingScroll } = useAutoScroll()
    const [isRequiresPurchaseTimeSlot, setIsRequiresPurchaseTimeSlot] =
        useState<boolean | undefined>(true)

    const [isModalOpened, setIsModalOpened] = useState(false)
    const [currentTicketDirection, setCurrentTicketDirection] =
        useState<DirectionType>('to_park')
    const [currentTicketDate, setCurrentTicketDate] = useState('')
    const [timeToOptions, setTimeToOptions] = useState<OptionProps[] | null>([
        { option: '' },
    ])
    const [timeFromOptions, setTimeFromOptions] = useState<
        OptionProps[] | null
    >([{ option: '' }])
    const [datesError, setDatesError] = useState<null | string>(null)
    const [hasMixedTimeSlotsError, setHasMixedTimeSlotsError] =
        useState<boolean>(false)
    const [wrongDatesRoutes, setWrongDatesRoutes] = useState<string[]>([])

    const todayDate = moment().format('YYYY-MM-DD')
    const isDateRangesError =
        !!dateTo &&
        !!dateFrom &&
        routeType === 'to_from_park' &&
        new Date(dateTo).getTime() > new Date(dateFrom).getTime()

    const transportData = useGetTransportAvailableDaysQuery({
        _locale: language,
    })

    const isDateInRange = (date: string, obj: ReadPresenter4) => {
        if (obj.from && obj.to) {
            return date >= obj.from && date <= obj.to
        }
        return false
    }

    const isInRange = (
        dateFrom: string | undefined,
        dateTo: string | undefined,
        obj: ReadPresenter4
    ) => {
        if (!dateFrom || !dateTo || !obj.from || !obj.to || !obj.excluded) {
            return false
        }

        const shouldBeInRange =
            isDateInRange(dateFrom, obj) && isDateInRange(dateTo, obj)
        const isExcluded =
            obj.excluded.includes(dateFrom) || obj.excluded.includes(dateTo)

        return shouldBeInRange && !isExcluded
    }

    const {
        data: schedules,
        isFetching,
        isError: isFetchingError,
        error,
        refetch,
    } = useGetTransportSchedulesQuery(
        {
            _locale: language,
            routeType,
            departurePlace,
            departureDate:
                routeType === 'from_park'
                    ? dateFrom || todayDate
                    : dateTo || todayDate,
            returnDate:
                routeType !== 'to_park'
                    ? dateFrom || todayDate
                    : dateTo || todayDate,
        },
        {
            skip:
                isDateRangesError ||
                !isInRange(dateFrom, dateTo, {
                    ...transportData.data,
                }),
        }
    )

    const checkIfSchedulesIsInRange = useCallback(() => {
        if (transportData.isFetching) return undefined

        return (
            !dateFrom ||
            !dateTo ||
            isInRange(dateFrom, dateTo, {
                ...transportData.data,
            })
        )
    }, [dateFrom, dateTo, transportData])

    useEffect(() => {
        const departureHours = schedules?.departureDestination?.hours
        const returnHours = schedules?.returnDestination?.hours

        if (isDateRangesError) {
            setDatesError(
                formatMessage(
                    {
                        id: 'upsells.transport.msg_incorrect_dates',
                        defaultMessage:
                            'Ta wartość powinna być większa bądź równa {limit_date}.',
                    },
                    {
                        limit_date: dateTo,
                    }
                )
            )
            setError && setError('dates')
            setWrongDatesRoutes([])
        } else if (
            // no buses at that day
            !departureHours ||
            departureHours.length === 0 ||
            (routeType === 'to_from_park' &&
                (!returnHours || !returnHours.length))
        ) {
            setError && setError('holidays')
        } else {
            setDatesError(null)
            setError && setError(null)
        }

        if (!schedules || isFetching) return

        formatTimeOptions(schedules)
    }, [isFetching, routeType, departurePlace, dateTo, dateFrom])

    useEffect(() => {
        // calculating mixed slots error
        if (!schedules) return

        const isDepartureTimeSlotRequired =
            schedules?.departureDestination?.isRequiresPurchaseTimeSlot
        const isReturnTimeSlotRequired =
            schedules?.returnDestination?.isRequiresPurchaseTimeSlot

        const areTimeSlotsMixed =
            isDepartureTimeSlotRequired !== undefined &&
            isReturnTimeSlotRequired !== undefined &&
            isDepartureTimeSlotRequired !== isReturnTimeSlotRequired

        setHasMixedTimeSlotsError(areTimeSlotsMixed)

        // calculating changing dates from one slot type to another error
        // (only selfcare)
        setWrongDatesRoutes([])

        if (areTimeSlotsMixed || isRequiresTimeSlotOriginal === undefined) {
            return
        }

        const tempWrongDatesRoutes: string[] = []

        if (isRequiresTimeSlotOriginal !== isDepartureTimeSlotRequired)
            tempWrongDatesRoutes.push(
                routeType !== 'from_park' ? 'to_park' : 'from_park'
            )

        if (
            isReturnTimeSlotRequired !== undefined &&
            isRequiresTimeSlotOriginal !== isReturnTimeSlotRequired
        )
            tempWrongDatesRoutes.push('from_park')

        // calculate if should block editing for tickets with incoherent timeslot values
        // (only selfcare - on initial edit opening)
        if (setIsEditable && isEditable === undefined) {
            setIsEditable(!tempWrongDatesRoutes.length)
            return
        }
        //

        setWrongDatesRoutes([...tempWrongDatesRoutes])
        setIsTimeSlotChangeable &&
            setIsTimeSlotChangeable(!tempWrongDatesRoutes.length)
    }, [schedules, departurePlace, routeType])

    useEffect(() => {
        if (!isFetchingError) {
            setDatesError(null)
            setError && setError(null)
            return
        }

        try {
            handleError({ error })
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            setError && setError('dates')
            messages &&
                messages.forEach((message) => {
                    if (message.property_path === 'returnDate') {
                        setDatesError(
                            message.message.replace(/\s\d{2}:\d{2}:\d{2}/, '')
                        )
                    }
                })
        }

        setWrongDatesRoutes([])
    }, [isFetchingError])

    useEffect(() => {
        refetch()
    }, [routeType, dateTo, dateFrom, isBookingConflictError])

    const handleDateChange = (direction: DirectionType, date: string) => {
        setCurrentTicketDirection(direction)
        setCurrentTicketDate(date)
        setIsModalOpened(true)
    }

    const handleDate = (date: string) => {
        currentTicketDirection === 'to_park'
            ? setDateTo &&
              setDateTo(routeIndex, date, isRequiresPurchaseTimeSlot || true)
            : setDateFrom &&
              setDateFrom(routeIndex, date, isRequiresPurchaseTimeSlot || true)
    }

    const formatTimeOptions = (data: BusSchedulePresenter, local = false) => {
        const {
            departureDestination,
            returnDestination,
            unitPrice,
            productId,
        } = data

        const departureHours = departureDestination?.hours
        const returnHours = returnDestination?.hours
        const isRequiresPurchaseTimeSlot =
            departureDestination?.isRequiresPurchaseTimeSlot

        let departureOptions: OptionProps[] | null = []

        let returnOptions: OptionProps[] | null = []

        if (returnHours && returnHours.length > 0) {
            returnHours.forEach((item) => {
                const { time, isAvailable, description, available } = item

                if (!time) return

                returnOptions !== null &&
                    returnOptions.push({
                        option: time,
                        isDisabled: !isAvailable,
                        optionInfo: description || undefined,
                        optionData: available || undefined,
                    })
            })
        } else returnOptions = null

        setTimeFromOptions(returnOptions)

        if (departureHours && departureHours.length > 0) {
            departureHours.forEach((item) => {
                const { time, isAvailable, description, available } = item

                if (!time) return

                departureOptions !== null &&
                    departureOptions.push({
                        option: time,
                        isDisabled: !isAvailable,
                        optionInfo: description || undefined,
                        optionData: available || undefined,
                    })
            })
        } else departureOptions = null

        if (routeType !== 'from_park') setTimeToOptions(departureOptions)
        else setTimeFromOptions(departureOptions)

        if (local) return
        if (isCreatorMode) animatePendingScroll('transport')

        setRouteDetails({
            routeIndex,
            unitPrice,
            productId,
            isRequiresPurchaseTimeSlot,
        })

        setIsRequiresPurchaseTimeSlot(isRequiresPurchaseTimeSlot)
    }

    // if arrival and departure on the same day, show return hours after arrival
    const limitTimeFromOptions = () => {
        if (routeType !== 'to_from_park' || timeFromOptions === null)
            return timeFromOptions

        return timeTo && dateTo === dateFrom
            ? timeFromOptions.filter(
                  (option) =>
                      getLaterTime(option.option, timeTo || '00:01') === 0
              )
            : timeFromOptions
    }

    const calculateOriginalSettings = (direction: 'to_park' | 'from_park') => {
        if (!edited) return

        const {
            quantity: originalQuantity,
            detail: {
                routeType: originRouteType,
                departurePlace: originDeparturePlace,
                date: originalDate,
                time: originalTime,
                returnDate: originalReturnDate,
                returnTime: originalReturnTime,
                isRequiresPurchaseTimeSlot: isRequiresPurchaseTimeSlotOriginal,
            },
        } = edited.origin.ticket

        // check if there is match between currently set options and the one in original route
        if (
            direction === 'to_park' &&
            routeType === originRouteType &&
            departurePlace === originDeparturePlace &&
            dateTo &&
            dateTo === originalDate &&
            originalTime &&
            originalQuantity
        ) {
            return {
                hour: originalTime,
                quantity: originalQuantity,
                isRequiresPurchaseTimeSlot: isRequiresPurchaseTimeSlotOriginal,
            }
        }

        if (
            direction === 'from_park' &&
            routeType === originRouteType &&
            departurePlace === originDeparturePlace &&
            dateFrom &&
            dateFrom === originalReturnDate &&
            originalReturnTime &&
            originalQuantity
        ) {
            return {
                hour: originalReturnTime,
                quantity: originalQuantity,
                isRequiresPurchaseTimeSlot: isRequiresPurchaseTimeSlotOriginal,
            }
        }

        return
    }

    const bookingConflictMsg = formatMessage({
        id: 'upsells.transport.msg_booking_conflict',
        defaultMessage:
            'Wygląda na to, że ktoś kupił ostatnie miejsca chwilę przed Tobą. Wybierz inną godzinę.',
    })

    return (
        <Box
            isFullWidth
            gap={[32, 40]}
            id={`transport-ticket-${routeId}-details`}
        >
            {routeType !== 'from_park' && (
                <BusDateAndHour
                    isInRange={checkIfSchedulesIsInRange()}
                    direction="to_park"
                    departurePlace={departurePlace}
                    ticketsQuantity={ticketsQuantity}
                    timeOptions={timeToOptions}
                    date={dateTo || ''}
                    onCalendarOpen={() => setIsModalOpened(true)}
                    onDateChange={() =>
                        handleDateChange('to_park', dateTo || '')
                    }
                    time={timeTo || ''}
                    onTimeChange={(value: string) =>
                        setTimeTo(
                            routeIndex,
                            value,
                            isRequiresPurchaseTimeSlot ?? true
                        )
                    }
                    onLimitChange={(limit) => setSeatsLimit(limit, 'to')}
                    isError={isFetchingError}
                    isBookingConflict={isBookingConflictError}
                    errorMsg={
                        isFetchingError || datesError
                            ? null
                            : bookingConflictMsg
                    }
                    isFetching={isFetching}
                    originalSettings={calculateOriginalSettings('to_park')}
                    isRequiresPurchaseTimeSlot={isRequiresPurchaseTimeSlot}
                    routeIndex={routeIndex}
                    testIdPrefix={testIdPrefix}
                    hasWrongDateError={wrongDatesRoutes.includes('to_park')}
                />
            )}
            {routeType !== 'to_park' && (
                <BusDateAndHour
                    isInRange={checkIfSchedulesIsInRange()}
                    direction="from_park"
                    departurePlace={departurePlace}
                    ticketsQuantity={ticketsQuantity}
                    timeOptions={limitTimeFromOptions()}
                    date={dateFrom || ''}
                    onCalendarOpen={() => setIsModalOpened(true)}
                    onDateChange={() =>
                        handleDateChange('from_park', dateFrom || '')
                    }
                    time={timeFrom || ''}
                    onTimeChange={(value: string) =>
                        setTimeFrom(
                            routeIndex,
                            value,
                            isRequiresPurchaseTimeSlot || true
                        )
                    }
                    onLimitChange={(limit) => setSeatsLimit(limit, 'from')}
                    isError={isFetchingError || isDateRangesError}
                    isBookingConflict={isBookingConflictError}
                    errorMsg={
                        isFetchingError
                            ? null
                            : datesError || bookingConflictMsg
                    }
                    isFetching={isFetching}
                    originalSettings={calculateOriginalSettings('from_park')}
                    isRequiresPurchaseTimeSlot={isRequiresPurchaseTimeSlot}
                    routeIndex={routeIndex}
                    testIdPrefix={testIdPrefix}
                    hasMixedTimeSlotsError={hasMixedTimeSlotsError}
                    hasWrongDateError={wrongDatesRoutes.includes('from_park')}
                />
            )}
            <BusesCalendarModal
                isOpened={isModalOpened}
                close={() => setIsModalOpened(false)}
                dateHandler={handleDate}
                routeType={currentTicketDirection}
                initialDate={currentTicketDate}
                errorHandler={(isError: boolean) => {
                    setError && setError(isError ? 'calendar' : null)
                }}
            />
        </Box>
    )
}
